// `extension-alert-client-contacts-slim.tpl.html:172` — one contact in the slim list.
//
// ⚠️ THE ROW IS THE ONE THING PHASE 4 CANNOT REUSE, and that is worth stating because
// everything around it can. The container, the filters bar, the selection bar, the alert
// selector, its dropdown panel, the tabs chrome and pagination are all shared with the
// candidate list — same classes, same components, a different entity. The row is not:
//
//   candidate   name / job title / "at company" / 📍 location
//   contact     name / COMPANY (bold, `info-item-main`) / job title (`info-item-secondary`)
//               / location (no map pin, `mT0`) / "Prev. engagement: …"
//
// Company and position swap places AND change classes, the map pin is gone, and the ATS
// engagement block has no candidate equivalent. Rendering contacts through `CandidateRow` with
// different props would put the job title where production puts the employer, in the weight
// production uses for the employer — a screen that looks right and reads wrong.
import type { ReactElement } from 'react'
import { WooCheckbox } from '../common/WooCheckbox.tsx'
import { timeSince } from '../common/timeSince.ts'
import { clientContactName, lastMessageDisplay, trimClientContactName } from './candidateName.ts'
import { hasPreviousEngagement, previousEngagementText } from './previousEngagement.ts'
import { ProfileActionsStrip } from './ProfileActionsStrip.tsx'
import type { ProfileActionDefinition } from './profileActions.ts'

/** The fields the row reads, from a live `getClientContactsByAlertId` response. */
export interface SlimClientContact {
  id: number
  /** ⚠️ OFTEN ABSENT — `clientContactName` supplies "Contact <externalId>" instead. */
  name?: string | null
  externalId?: string | null
  algorithm?: number
  lastPosition?: string | null
  lastCompany?: string | null
  location?: string | null
  hidden?: boolean
  viewed?: boolean
  /** Epoch millis of the last message received; "Never" when there is none. */
  lastReceivedMessage?: number | null
  exported?: boolean
  atsInfo?: {
    contactHighestActivityLevel?: string | null
    contactHighestActivityCount?: number | null
    /** Epoch millis. ⚠️ The contact row's date is THIS, not a CV date — see the row. */
    contactLastActivityDate?: number | null
  } | null
}

export interface ClientContactRowProps {
  contact: SlimClientContact
  checked?: boolean
  previouslySelected?: boolean
  /** `messageCandidatesEnabled` — the same candidate flag; contacts have none of their own. */
  messagingEnabled?: boolean
  showAlgorithm?: boolean
  onOpen?: () => void
  onCheck?: (checked: boolean) => void
  /**
   * The envelope — opens `MessageHistoryTooltip`. Called only when there IS a last message;
   * the click is swallowed either way.
   */
  onOpenMessages?: () => void
  /** One clock for the whole list, as on the candidate side. */
  now?: number
  /**
   * The hover strip — `extension-alert-entities-profile-actions max-action-amount="6"`, the
   * same element the candidate row carries (`…-client-contacts-slim.tpl.html:252`).
   */
  actions?: readonly ProfileActionDefinition[]
  onAction?: (action: ProfileActionDefinition) => void
  /** `previewClientContactResume` — rendered only with a handler, as on the candidate row. */
  onPreviewResume?: () => void
  /** `!$state.current.data.isCheckboxDependent` gates the strip and the chevron together. */
  checkboxDependent?: boolean
}

export function ClientContactRow({
  contact,
  checked = false,
  previouslySelected = false,
  messagingEnabled = false,
  showAlgorithm = false,
  onOpen,
  onCheck,
  onOpenMessages,
  actions,
  onAction,
  onPreviewResume,
  checkboxDependent = false,
  now,
}: ClientContactRowProps): ReactElement {
  const ats = contact.atsInfo
  const showsEngagement = hasPreviousEngagement(ats)

  return (
    <div
      className={[
        'shortlist-candidate-slim',
        `cand-${String(contact.id)}`,
        checked ? 'checked' : '',
        previouslySelected ? 'previously-selected' : '',
        contact.hidden === true ? 'is-hidden' : '',
        contact.viewed === true ? 'is-viewed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {checkboxDependent ? null : (
        <div className="shortlist-hover-open-resume">
          <a
            className="open-resume-action-btn"
            href=""
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onPreviewResume?.()
            }}
          >
            <i className="fal fa-chevron-right" />
          </a>
        </div>
      )}
      <div
        className="shortlist-candidate-slim-details-container cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          onOpen?.()
        }}
      >
        <div className="left">
          <div className="candidate-slim-details-checkbox-container">
            <WooCheckbox
              className="candidate-slim-details-checkbox"
              resetStyle
              checked={checked}
              stopPropagation
              onChange={(next) => onCheck?.(next)}
            />
          </div>
        </div>
        <div className="right">
          {/* ⚠️ THIS SECTION WAS MISSING ENTIRELY, and its absence pulled every row up by its
              own height — which looked like a list-spacing difference rather than an omitted
              row of content. It differs from the candidate row's in exactly three ways, all of
              them easy to copy wrong:

                - the actioned cell has NO briefcase. A contact is never "added to a job", so
                  only the upload survives, and it is NOT gated on `addedToJob` here;
                - the date is `atsInfo.contactLastActivityDate`, NOT a CV date — a contact has
                  no CV. The class is still `alert-abbreviated-cv-date`, which is what makes
                  the candidate version look like the right thing to copy;
                - its title is the literal "Previous engagement", not a computed tooltip.

              The envelope cell itself is identical, down to reading the CANDIDATE message
              flag: contacts have no flag of their own. */}
          <div className="candidate-slim-details-top-section">
            {messagingEnabled ? (
              // `ng-click="ctrl.open…MessagesTooltip(…, $event)"` — the Message History.
              // ⚠️ THE CLICK IS STOPPED EVEN WHEN THERE IS NO HISTORY. Upstream's handler calls
              // `stopPropagation()` before its `!lastReceivedMessage` early return, so a click on
              // "Never" does nothing at all — it does not fall through and open the row.
              <div
                onClick={(event) => {
                  event.stopPropagation()
                  if (!contact.lastReceivedMessage) return
                  onOpenMessages?.()
                }}
              >
                <i className="fas fa-envelope" /> {lastMessageDisplay(contact.lastReceivedMessage)}
              </div>
            ) : null}
            <div className="candidate-slim-actioned">
              {contact.exported === true ? <i className="fas fa-upload" title="Exported" /> : null}
            </div>
            <div className="alert-abbreviated-cv-date" title="Previous engagement">
              {ats?.contactLastActivityDate === undefined || ats.contactLastActivityDate === null
                ? null
                : timeSince(ats.contactLastActivityDate, true, now)}
            </div>
          </div>
          <div className="candidate-slim-details-info-section">
            <div className="candidate-slim-details-name-experience-info-section">
              <div
                className={`cursor-pointer shortlist-candidate-slim-name algo-${String(contact.algorithm ?? '')}`}
                title={clientContactName(contact)}
              >
                {trimClientContactName(contact)}
                {showAlgorithm ? <span> ({contact.algorithm})</span> : null}
              </div>
              {contact.lastCompany === undefined ||
              contact.lastCompany === null ||
              contact.lastCompany === '' ? null : (
                <div className="shortlist-candidate-slim-info-item info-item-main soft-bold-important">
                  {contact.lastCompany}
                </div>
              )}
              <div className="shortlist-candidate-slim-info-item info-item-secondary">{contact.lastPosition}</div>
              {contact.location === undefined || contact.location === null || contact.location === '' ? null : (
                // `into-item-location` — upstream's typo again, and `mT0` here where the
                // candidate row has no margin class. No map pin on this side.
                <div className="shortlist-candidate-slim-info-item into-item-location mT0" title={contact.location}>
                  {contact.location}
                </div>
              )}
              {showsEngagement ? (
                <div className="shortlist-candidate-slim-info-item-cont mT10">
                  <div className="soft-bold-important">Prev. engagement:</div>
                  {/* `getPreviousEngagementText` REWRITES the level rather than printing it:
                      "Had placements" with a count of 2 reads "2 placements". An earlier
                      version rendered the raw level, which showed the right words for the
                      wrong screen and no number at all. */}
                  <div
                    className="shortlist-candidate-slim-info-item into-item-prev-engagement"
                    title={previousEngagementText(ats ?? {})}
                  >
                    {previousEngagementText(ats ?? {})}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {/* The contacts twin of the candidate row's strip — same two classes, same component,
          and it was missing for the same reason: `ProfileActionsStrip` was ported with the
          snippets and neither row was given it. */}
      {checkboxDependent || actions === undefined || actions.length === 0 ? null : (
        <div className="shortlist-candidate-slim-hover alert-candidate-bottom-section">
          <ProfileActionsStrip entity={contact} actions={actions} maxActionAmount={6} onAction={onAction} />
        </div>
      )}
    </div>
  )
}
